/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  EntityType,
  NotificationGroupType,
  NotificationRecipientType,
  NotificationTriggerType,
  NotificationType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationTemplateProps {
  id: string;
  name: string;
  companyId: string | null;
  triggerType: NotificationTriggerType | null;
  triggerValue: string | null;
  activationRuleId: string | null;
  recipientTypes: Set<NotificationRecipientType> | null;
  contextType: EntityType;
  subjectTemplate: string | null;
  bodyTemplate: string;
  requiresApproval: boolean | null;
  groupTypes: Set<NotificationGroupType> | null;
  canBeManualTrigger: boolean;
  createdById: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationTemplateRecord extends Record<NotificationTemplateProps>({
  id: null as any,
  name: null as any,
  companyId: null as any,
  triggerType: null as any,
  triggerValue: null as any,
  activationRuleId: null as any,
  recipientTypes: null as any,
  contextType: null as any,
  subjectTemplate: null as any,
  bodyTemplate: null as any,
  requiresApproval: null as any,
  groupTypes: null as any,
  canBeManualTrigger: null as any,
  createdById: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    companyId: string | null;
    triggerType: NotificationTriggerType | null;
    triggerValue: string | null;
    activationRuleId: string | null;
    recipientTypes: any;
    contextType: EntityType;
    subjectTemplate: string | null;
    bodyTemplate: string;
    requiresApproval: boolean | null;
    groupTypes: any;
    canBeManualTrigger: boolean;
    createdById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationTemplateRecord {
    if (json == null) {
      throw new Error('NotificationTemplateRecord.fromJS: parameter cannot be null');
    }
    return new NotificationTemplateRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationTemplateJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NotificationTemplateJson.name'),
      companyId: json.companyId,
      triggerType: json.triggerType,
      triggerValue: json.triggerValue,
      activationRuleId: json.activationRuleId,
      recipientTypes:
        json.recipientTypes != null ? Set<NotificationRecipientType>(json.recipientTypes) : null,
      contextType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'contextType', EntityType.Unspecified),
        'NotificationTemplateJson.contextType',
      ),
      subjectTemplate: json.subjectTemplate,
      bodyTemplate: getFromOrThrow(
        json,
        (j) => j.bodyTemplate,
        'NotificationTemplateJson.bodyTemplate',
      ),
      requiresApproval: json.requiresApproval,
      groupTypes: json.groupTypes != null ? Set<NotificationGroupType>(json.groupTypes) : null,
      canBeManualTrigger: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'canBeManualTrigger', false),
        'NotificationTemplateJson.canBeManualTrigger',
      ),
      createdById: json.createdById,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    companyId: string | null;
    triggerType: NotificationTriggerType | null;
    triggerValue: string | null;
    activationRuleId: string | null;
    recipientTypes: any;
    contextType: EntityType;
    subjectTemplate: string | null;
    bodyTemplate: string;
    requiresApproval: boolean | null;
    groupTypes: any;
    canBeManualTrigger: boolean;
    createdById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationTemplateRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationTemplateRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationTemplateRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationTemplateJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NotificationTemplateJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      triggerType: tryOrDefault(
        () => json.triggerType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      triggerValue: tryOrDefault(
        () => json.triggerValue,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationRuleId: tryOrDefault(
        () => json.activationRuleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      recipientTypes: tryOrDefault(
        () =>
          json.recipientTypes != null ? Set<NotificationRecipientType>(json.recipientTypes) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      contextType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'contextType', EntityType.Unspecified),
            'NotificationTemplateJson.contextType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subjectTemplate: tryOrDefault(
        () => json.subjectTemplate,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      bodyTemplate: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.bodyTemplate, 'NotificationTemplateJson.bodyTemplate'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiresApproval: tryOrDefault(
        () => json.requiresApproval,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      groupTypes: tryOrDefault(
        () => (json.groupTypes != null ? Set<NotificationGroupType>(json.groupTypes) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeManualTrigger: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'canBeManualTrigger', false),
            'NotificationTemplateJson.canBeManualTrigger',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationTemplateProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationTemplateRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationTemplateViewModelBase implements NotificationTemplateProps {
  protected readonly _props: NotificationTemplateProps;
  private _json?: NotificationTemplateProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }
  get triggerType(): NotificationTriggerType | null {
    return this._props.triggerType;
  }
  get triggerValue(): string | null {
    return this._props.triggerValue;
  }
  get activationRuleId(): string | null {
    return this._props.activationRuleId;
  }
  get recipientTypes(): Set<NotificationRecipientType> | null {
    return this._props.recipientTypes;
  }
  get contextType(): EntityType {
    return this._props.contextType;
  }
  get subjectTemplate(): string | null {
    return this._props.subjectTemplate;
  }
  get bodyTemplate(): string {
    return this._props.bodyTemplate;
  }
  get requiresApproval(): boolean | null {
    return this._props.requiresApproval;
  }
  get groupTypes(): Set<NotificationGroupType> | null {
    return this._props.groupTypes;
  }
  get canBeManualTrigger(): boolean {
    return this._props.canBeManualTrigger;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isActivity() {
    return this._props.triggerType === NotificationTriggerType.Activity;
  }
  public get isManual() {
    return this._props.triggerType === NotificationTriggerType.Manual;
  }
  public get isAirport() {
    return this._props.contextType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.contextType === EntityType.Base;
  }
  public get isCard() {
    return this._props.contextType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.contextType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.contextType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.contextType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.contextType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.contextType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.contextType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.contextType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.contextType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.contextType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.contextType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.contextType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.contextType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.contextType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.contextType === EntityType.Vehicle;
  }

  public isAnyTriggerType(...notificationTriggerType: NotificationTriggerType[]) {
    return (
      this._props.triggerType != null && notificationTriggerType.includes(this._props.triggerType)
    );
  }
  public isAnyContextType(...entityType: EntityType[]) {
    return entityType.includes(this._props.contextType);
  }

  constructor(props: NotificationTemplateProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        companyId: this._props.companyId,
        triggerType: this._props.triggerType,
        triggerValue: this._props.triggerValue,
        activationRuleId: this._props.activationRuleId,
        recipientTypes: this._props.recipientTypes,
        contextType: this._props.contextType,
        subjectTemplate: this._props.subjectTemplate,
        bodyTemplate: this._props.bodyTemplate,
        requiresApproval: this._props.requiresApproval,
        groupTypes: this._props.groupTypes,
        canBeManualTrigger: this._props.canBeManualTrigger,
        createdById: this._props.createdById,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationTemplateEntities = (state: AppState) =>
  state.entityStores.notificationTemplate.entities;

export const selectNotificationTemplateById = (id: string) => (state: AppState) =>
  state.entityStores.notificationTemplate.entities.get(id);

export const selectNotificationTemplateMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notificationTemplate.meta.get(id);

export const selectNotificationTemplateAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationTemplateById(id),
    selectNotificationTemplateMetadataById(id),
    (notificationTemplate, meta) => ({ notificationTemplate, meta }),
  );

export function selectNotificationTemplateEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationTemplateEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationTemplateRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationTemplateEntitiesByName(name: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByTriggerType(
  triggerType: NotificationTriggerType,
) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.triggerType === triggerType).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByTriggerValue(triggerValue: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.triggerValue === triggerValue).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByActivationRuleId(activationRuleId: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.activationRuleId === activationRuleId).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByContextType(contextType: EntityType) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.contextType === contextType).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesBySubjectTemplate(subjectTemplate: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.subjectTemplate === subjectTemplate).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByBodyTemplate(bodyTemplate: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.bodyTemplate === bodyTemplate).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByRequiresApproval(requiresApproval: boolean) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.requiresApproval === requiresApproval).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByCanBeManualTrigger(
  canBeManualTrigger: boolean,
) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.canBeManualTrigger === canBeManualTrigger).valueSeq(),
  );
}

export function selectNotificationTemplateEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllNotificationTemplateEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}
//#endregion Selectors
